import {
    HordeAuthorityDocumentSchema,
    HordeGameIntentDocumentSchema,
    HordePlayerSecretViewDocumentSchema,
    PersistedHordeGameSchema,
    PlayerSecretViewStateSchema,
    type HostSecretVaultState,
    type PlayerSecretViewState,
    type ValidatedHordeGameIntentDocument,
} from '@animus/type/domain';
import type {
    HordeAuthorityState,
    HordeSessionLoadResult,
    HordeSessionSummary,
} from '@animus/utility/web/persistence';
import { withoutRxdbMetadata } from './rxdb-document.util';

/**
 * Pure projections between stored RxDB documents and Horde session state.
 *
 * Nothing here touches the database, so every clean-schema validation decision is testable without a storage
 * adapter. This release intentionally has no compatibility migration: previous databases are isolated by name.
 */

/** The only part of an `RxDocument` these projections read, so tests can pass a plain object. */
export interface ReadableRxDocument {
    toMutableJSON(): unknown;
}

/** Drops invalid documents from the home index while never exposing private authority state. */
export function toPublicSessionSummaries(docs: readonly ReadableRxDocument[]): HordeSessionSummary[] {
    return docs.flatMap((doc) => {
        const parsed = PersistedHordeGameSchema.safeParse(withoutRxdbMetadata(doc.toMutableJSON()));

        return parsed?.success
            ? [
                  {
                      gameId: parsed.data.gameId,
                      lifecycle: parsed.data.lifecycle,
                      name: parsed.data.name,
                      players: parsed.data.players.length,
                      revision: parsed.data.revision,
                      updatedAt: parsed.data.lastCommittedAt,
                  },
              ]
            : [];
    });
}

/** Combines persisted public game and host vault documents into authority state. */
export function toAuthorityState(doc: ReadableRxDocument | null): HordeAuthorityState | null {
    if (!doc) {
        return null;
    }

    const parsed = HordeAuthorityDocumentSchema.safeParse(withoutRxdbMetadata(doc.toMutableJSON()));

    return parsed?.success
        ? {
              game: parsed.data.game,
              vault: parsed.data.vault,
          }
        : null;
}

/** Projects a persisted private player document into the session’s secret view. */
export function toPlayerSecretView(doc: ReadableRxDocument | null): PlayerSecretViewState | null {
    if (!doc) {
        return null;
    }

    const parsed = HordePlayerSecretViewDocumentSchema.safeParse(withoutRxdbMetadata(doc.toMutableJSON()));
    // safeParse for the projection too: this runs inside live RxDB query streams, where a thrown zod error
    // would terminate the observable and silently freeze the session's secret view.
    const view = parsed?.success ? PlayerSecretViewStateSchema.safeParse(parsed.data) : null;

    return view?.success ? view.data : null;
}

/** Creates player secret views from the supplied validated inputs. */
export function createPlayerSecretViews(vault: HostSecretVaultState): PlayerSecretViewState[] {
    return Object.entries(vault.players).map(([playerId, secret]) => ({
        completed: secret.completed,
        gameId: vault.gameId,
        heldCardId: secret.heldCardId,
        offeredCardIds: [...secret.offeredCardIds],
        playerId,
        privateActionIds: [...secret.privateActionIds],
        privateTallies: { ...secret.privateTallies },
        revealed: secret.revealed,
        revision: vault.revision,
        sourceVaultRevision: vault.revision,
    }));
}

/** Classifies stored public state against this release's clean ID-only schema. */
export function classifyPublicSession(value: unknown): HordeSessionLoadResult {
    const schemaVersion =
        value !== null && typeof value === 'object' && 'schemaVersion' in value
            ? (value as { schemaVersion?: unknown }).schemaVersion
            : undefined;

    if (typeof schemaVersion === 'number' && schemaVersion > 1) {
        return { preservedValue: value, schemaVersion, status: 'incompatible' };
    }

    const parsed = PersistedHordeGameSchema.safeParse(withoutRxdbMetadata(value));

    return parsed?.success
        ? { status: 'ready', value: parsed.data }
        : { preservedValue: value, status: 'corrupt' };
}

/** Parses pending outbox documents and omits entries that fail Horde intent validation. */
export function parsePendingIntents(docs: readonly ReadableRxDocument[]): ValidatedHordeGameIntentDocument[] {
    return docs.flatMap((doc) => {
        const parsed = HordeGameIntentDocumentSchema.safeParse(withoutRxdbMetadata(doc.toMutableJSON()));

        return parsed?.success ? [parsed.data] : [];
    });
}
